import { FormikSelect, theme } from '@vfs-digital-channels/ns-react-components';
import {
  Field,
  Form,
  Formik
} from 'formik';
import React, {
  useEffect,
  useMemo,
  useRef
} from 'react';
import {
  useLocation,
  useNavigate
} from 'react-router-dom';
import { useRecoilCallback, useRecoilValue, useResetRecoilState } from 'recoil';
import { ThemeProvider } from 'styled-components';
import * as yup from 'yup';
import { StaticRoutes } from '../../../Router';
import {
  AddressDetailsComponent,
  Footer,
  addressValidationSchema
} from '../../../components';
import { DateSelector } from '../../../components/DateSelector/DateSelector';
import {
  Colors,
  CustomFormField,
  Fonts,
  FormContainer,
  FormField,
  FormFieldError,
  FormFieldWrapper,
  Provinces,
  ResidentialStatus
} from '../../../constants';
import {
  CountryListInput,
  mapValues,
  stripEmpty
} from '../../../helpers';
import { findFirstValidationError } from '../../../helpers/formik';
import { useFindErrorOnState } from '../../../hooks/useFindErrorOnState';
import {
  personalDetailsState,
  personalDetailsSubmit,
  toasts,
  useIsMobile,
  useShowToast
} from '../../../recoil';
import { useTealium } from '../../../tealium/useTealium';

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    marginBottom: 60
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: '57px 0 23px 0',
    width: '100%'
  },
  titleHeading: {
    display: 'flex',
    justifyContent: 'center',
    textAlign: 'center',
    fontFamily: Fonts.RobotoMedium,
    fontStyle: 'normal',
    fontSize: '1.500em',
    lineHeight: '30px',
    color: Colors.GreyScale12,
    width: 'inherit'
  },
  formContainer: {
    ...FormContainer,
    margin: '0 0 100px 0'
  }
};

const validationSchema = yup.object({
  address: addressValidationSchema,
  residentialStatus: yup.mixed().test('residentialStatus', 'Residential Status is required', (item) => !!item?.value),
  whenMovedIn: yup.date().max(new Date(), 'Date should be in past').required('Current Residence Time is required')
});

export const ProfilePersonalAddress = () => {

  const details = useRecoilValue(personalDetailsSubmit);
  const navigate = useNavigate();
  const location = useLocation();
  const formRef = useRef();
  const showToast = useShowToast();
  const isMobile = useRecoilValue(useIsMobile);
  const resetToast = useResetRecoilState(toasts);
  const reset = useResetRecoilState(personalDetailsState);
  useFindErrorOnState([personalDetailsSubmit], () => {
    reset();
  });
  const { track, events } = useTealium();

  const formDetails = useMemo(() => ({
    ...details,
    address: {
      ...details.address,
      province: Provinces.find((p) => p.value === details.address.province),
      country: CountryListInput.find((c) => c.value === (details.address.country || 'ZA'))
    },
    residentialStatus: ResidentialStatus.find((r) => r.value === details.residentialStatus)
  }), [details]);

  const detailsPromise = useRecoilCallback(({ snapshot, set }) => async (data) => {
    set(personalDetailsSubmit, data);
    return await snapshot.getPromise(personalDetailsSubmit);
  }, []);

  const formatFormData = (formValues) => mapValues(stripEmpty({
    ...formValues,
    address: Object.keys(formValues.address).reduce((accum, key) => ({
      ...accum,
      [key]: formValues.address[key]?.value || formValues.address[key]
    }), {}),
    residentialStatus: formValues.residentialStatus.value,
    whenMovedIn: formValues.whenMovedIn
  }));

  const formSubmit = async (formValues) => {

    const submission = formatFormData(formValues);
    const data = await detailsPromise(submission);
    showToast('Updated successfully');
    return data;
  };

  const goToMainPage = () => {
    navigate(StaticRoutes.profile);
  };

  const handleSubmit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  };

  useEffect(() => {
    if (details.error) {
      resetToast();
      return;
    }
  }, [details]);

  const handleFormikTouchedErrors = (touched, errors) => {
    const errorMessage = findFirstValidationError(errors, touched);

    if (!errorMessage) {
      return;
    }

    track(events.error('form', location.pathname, errorMessage));
  };

  return (
    <div style={styles.container}>
      <div style={styles.titleContainer}>
        <div style={styles.titleHeading}>
          Address details
        </div>
      </div>

      <ThemeProvider theme={theme.light}>
        <Formik
          initialValues={formDetails}
          onSubmit={formSubmit}
          validationSchema={validationSchema}
          validateOnMount={true}
          innerRef={formRef}
        >
          {({ errors, touched }) => {
            handleFormikTouchedErrors(touched, errors);
            return (
              <Form style={styles.formContainer}>

                <div style={FormFieldWrapper}>

                  <AddressDetailsComponent />

                  <div style={FormField}>

                    <FormikSelect
                      label='Residential Status'
                      name='residentialStatus'
                      options={ResidentialStatus}
                      scrollToTop={false} />
                  </div>

                  <div style={errors?.whenMovedIn ? FormFieldError : CustomFormField}>
                    <Field
                      name='whenMovedIn'
                      component={DateSelector}
                      label='When did you move to this address?' />
                  </div>
                </div>

                <Footer leftCallback={goToMainPage} rightCallback={Object.values(errors).length ? null : handleSubmit} leftTitle={isMobile ? '' : 'Cancel'} rightTitle={'Save'} />
              </Form>
            )
          }}
        </Formik>
      </ThemeProvider>

    </div>
  );
};